/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { withApollo } from 'react-apollo';

import type { ApolloClient } from '@apollo/client';
import { enableAppearancePreferences } from '@coursera-web/appearance';
import { PersonalizationTab, useIsPersonalizationSettingsEnabled } from '@coursera-web/personalization-settings';
import { compose, setDisplayName, withProps } from 'recompose';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import type { InjectedRouter } from 'js/lib/connectToRouter';
import connectToRouter from 'js/lib/connectToRouter';
import Retracked from 'js/lib/retracked';
import type { UserData } from 'js/lib/user';
import user from 'js/lib/user';
import waitForGraphQL from 'js/lib/waitForGraphQL';

import { Button, PageNotification, Tab, TabContext, TabPanel, Tabs, Typography2 } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';
import type { UserEmail } from '@coursera/grpc-types-useremails/coursera/proto/useremails/v1/email_address';

import {
  getUserSettings,
  resetAllSections,
  setTopSection,
  setUserData,
} from 'bundles/account-settings/actions/SettingsActionCreators';
import AccountSettingsPageProviders from 'bundles/account-settings/components/AccountSettingsPageProviders';
import SectionCard from 'bundles/account-settings/components/SectionCard';
import AccountLinkingPreferences from 'bundles/account-settings/components/sections/account-linking/AccountLinkingPreferences';
import SoundEffectsField, {
  DEEP_LINK_HASH as SOUND_EFFECTS_HASH,
} from 'bundles/account-settings/components/sections/appearance-preferences/SoundEffectsField';
import AppearancePreferences from 'bundles/account-settings/components/sections/appearance-preferences/v2/AppearancePreferences';
import BasicInformation from 'bundles/account-settings/components/sections/basic-information/BasicInformation';
import CalendarSettings from 'bundles/account-settings/components/sections/calendar/CalendarSettings';
import DataReport from 'bundles/account-settings/components/sections/data-report/DataReport';
import AccountDeletion from 'bundles/account-settings/components/sections/delete-account/AccountDeletion';
import DeletionConfirmation from 'bundles/account-settings/components/sections/delete-account/DeletionConfirmation';
import EmailPreferences from 'bundles/account-settings/components/sections/email-preferences/EmailPreferences';
import NotesAndHighlightingSettings from 'bundles/account-settings/components/sections/highlights/NotesAndHighlightingSettings';
import CreditCardSettings from 'bundles/account-settings/components/sections/payment-method/CreditCardSettings';
import ProfilePhoto from 'bundles/account-settings/components/sections/profile-photo/ProfilePhoto';
import RecoveryEmail from 'bundles/account-settings/components/sections/recovery-email/RecoveryEmail';
import AlternativeEmail from 'bundles/account-settings/components/sections/reset-password/AlternativeEmail';
import Password from 'bundles/account-settings/components/sections/reset-password/Password';
import SessionInvalidation from 'bundles/account-settings/components/sections/session-invalidation/SessionInvalidation';
import TwoFactorAuthentication from 'bundles/account-settings/components/sections/two-factor/Settings';
import UserContextSwitcherSection from 'bundles/account-settings/components/sections/user-context-switcher/UserContextSwitcherSection';
import { TabScrollAffordance, useTabScrollAffordance } from 'bundles/account-settings/components/tab-affordance';
import { Sections, TopSections, UpdateStates } from 'bundles/account-settings/constants/SettingsConstants';
import type { EnterpriseUserFeaturesContextType } from 'bundles/account-settings/contexts/EnterpriseUserFeaturesContext';
import {
  EnterpriseUserFeaturesConsumer,
  EnterpriseUserFeaturesProvider,
} from 'bundles/account-settings/contexts/EnterpriseUserFeaturesContext';
import withRecoveryEmail from 'bundles/account-settings/hocs/withRecoveryEmail';
import type {
  FindUserEmailsByUserIdQueryResult,
  FindUserEmailsByUserIdQueryVariables,
} from 'bundles/account-settings/queries';
import { FindUserEmailsByUserIdQuery } from 'bundles/account-settings/queries';
import ProgramMembershipsAndThirdPartyAccountsQuery from 'bundles/account-settings/queries/ProgramMembershipsAndThirdPartyAccountsQuery.graphql';
import type {
  ProgramMembershipsAndThirdPartyAccountsQueryQuery,
  ProgramMembershipsAndThirdPartyAccountsQueryQueryVariables,
} from 'bundles/account-settings/queries/__generated__/ProgramMembershipsAndThirdPartyAccountsQuery';
import SettingsStore from 'bundles/account-settings/stores/SettingsStore';
import type {
  CourseEmailPreference,
  GlobalChannelNotificationPreferences,
  PartnerEmailPreference,
} from 'bundles/account-settings/types/EmailPreferences';
import type { PaymentWallet } from 'bundles/account-settings/types/Payment';
import type { RecoveryEmailData } from 'bundles/account-settings/types/RecoveryEmail';
import type {
  FieldValue,
  SectionValueType,
  TopSectionValueType,
  UpdateStatusResult,
} from 'bundles/account-settings/types/Settings';
import { getSecondaryEmail } from 'bundles/account-settings/utils/RecoveryEmailUtils';
import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import AccountPageHeader from 'bundles/composable-page-header-consumer/header-renderers/AccountPageHeader';
import UniversalPageFeatures from 'bundles/composable-page-header/components/UniversalPageFeatures';
import { useIsDailyGoalsFeatureEnabled } from 'bundles/daily-goals/features/hooks/useIsDailyGoalsFeatureEnabled';
import Naptime from 'bundles/naptimejs';
import GdprSettings from 'bundles/naptimejs/resources/gdprSettings.v1';
import type { NotificationPreferencesV2Type } from 'bundles/naptimejs/resources/notificationPreferences.v2';
import type ThirdPartyOrganizationsV1 from 'bundles/naptimejs/resources/thirdPartyOrganizations.v1';
import ThirdPartyOrganizationsV1Resource from 'bundles/naptimejs/resources/thirdPartyOrganizations.v1';
import PageFooter from 'bundles/page-footer/components/PageFooter';
import CourseraMetatags from 'bundles/seo/components/CourseraMetatags';
import { isMyLearningRedesignEnabled } from 'bundles/unified-home-common/utils/myLearningRedesignEpicFeatureFlags';
import JumioVerificationSettings from 'bundles/verification/components/cds/JumioVerificationSettings';

import _t from 'i18n!nls/account-settings';

type Props = {
  userData: UserData;
  showPersonalAccountSection: boolean;
  showPasswordSection: boolean;
  showAccountDeletionSection: boolean;
  secondaryEmail: UserEmail | undefined;
  hasGeneratedPassword: boolean;
  router: InjectedRouter;
  client: ApolloClient<unknown>;
  ssoOrg: ThirdPartyOrganizationsV1 | undefined;
  recoveryEmailData?: RecoveryEmailData | null;
  setRecoveryEmailData?: (data: RecoveryEmailData | null) => void;
  thirdPartyOrganizationIds?: string[];
};

export type SettingsAppProps = Props & EnterpriseUserFeaturesContextType;

type StoreState = {
  emailAddress?: string;
  fullName?: string;
  locale?: string;
  timezone?: string;
  userId?: number;
  isAccountDeleted?: boolean;
  hasPendingChanges: boolean;
  topSection?: string;
  notificationPreferences?: NotificationPreferencesV2Type | null;
  updateStatus: UpdateStatusResult;
  invalidFields?: FieldValue[] | null;
  updatedEmailAddress?: string | null;
  creditCardPreferences?: Pick<
    PaymentWallet,
    'id' | 'creditCardExpiration' | 'creditCardLastFourDigits' | 'creditCardType' | 'isValid'
  > | null;
  wallets?: PaymentWallet[] | null;
  showDeleteCreditCardModal?: boolean;
  activeWalletId?: number | null;
  emailPreferences?: {
    courses: CourseEmailPreference[];
    partners: PartnerEmailPreference[];
    coursera: GlobalChannelNotificationPreferences;
  } | null;
  twoFactorAuthenticationStatus?: string;
  twoFactorAuthenticationQRCode?: string | null;
  twoFactorAuthenticationInvalidFields?: string[];
  twoFactorAuthenticationIsUpdating?: boolean;
  twoFactorAuthenticationSuccess?: boolean;
};

const styles = {
  root: css`
    background-color: var(--cds-color-neutral-background-primary);
  `,
  container: css`
    max-width: 658px;
    width: 100%;
    margin: 0 auto;
    padding: var(--cds-spacing-400) var(--cds-spacing-200) var(--cds-spacing-300);
  `,
  recoveryEmailWarning: css`
    margin-block-end: var(--cds-spacing-300);
  `,
  heading: css`
    && {
      margin-bottom: 8px;
    }
  `,
  tabsWrapper: css`
    position: relative;
    margin-bottom: var(--cds-spacing-300);
  `,
  tabs: css`
    button {
      white-space: nowrap;
      max-width: none;
    }
  `,
  sections: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
  `,
};

const isValidTopSection = (value: string): value is TopSectionValueType =>
  (Object.values(TopSections) as string[]).includes(value);

const ACCOUNT_SETTINGS_TABS_NAME = 'account_settings_tabs';

const getUpdateStatus = (updateStatus: UpdateStatusResult, section: SectionValueType): UpdateStatusResult => {
  return updateStatus.section === section ? updateStatus : { state: UpdateStates.IDLE };
};

export const SettingsApp = ({
  userData,
  router,
  client,
  showPersonalAccountSection,
  showPasswordSection,
  showAccountDeletionSection,
  secondaryEmail,
  hasGeneratedPassword,
  isUserSsoLoginForced,
  ssoOrg,
  isUnifiedSso,
  gdprSettings,
  recoveryEmailData,
  setRecoveryEmailData,
}: SettingsAppProps) => {
  const isPersonalizationEnabled = useIsPersonalizationSettingsEnabled();

  const [storeState, setStoreState] = useState<StoreState>({
    emailAddress: userData.email_address,
    fullName: userData.fullName,
    locale: userData.locale,
    timezone: userData.timezone,
    userId: userData.id,
    isAccountDeleted: false,
    hasPendingChanges: false,
    updateStatus: { state: UpdateStates.IDLE },
  });

  const onChange = useCallback<() => void>(() => {
    try {
      setStoreState({
        topSection: SettingsStore.getTopSection(),
        notificationPreferences: SettingsStore.getNotificationPreferences(),
        userId: SettingsStore.getUserId(),
        fullName: SettingsStore.getFullName(),
        emailAddress: SettingsStore.getEmailAddress(),
        timezone: SettingsStore.getTimezone(),
        locale: SettingsStore.getLocale(),
        updateStatus: SettingsStore.getUpdateStatus(),
        invalidFields: SettingsStore.getInvalidFields(),
        updatedEmailAddress: SettingsStore.getUpdatedEmailAddress(),
        creditCardPreferences: SettingsStore.getCreditCardPreferences(),
        wallets: SettingsStore.getPaymentWallets(),
        showDeleteCreditCardModal: SettingsStore.getShowDeleteCreditCardModal(),
        activeWalletId: SettingsStore.getActiveWalletId(),
        emailPreferences: SettingsStore.getEmailPreferences(),
        hasPendingChanges: SettingsStore.hasPendingChanges(),
        twoFactorAuthenticationStatus: SettingsStore.getTwoFactorAuthenticationStatus(),
        twoFactorAuthenticationQRCode: SettingsStore.getTwoFactorAuthenticationQRCode(),
        twoFactorAuthenticationInvalidFields: SettingsStore.getTwoFactorAuthenticationInvalidFields(),
        twoFactorAuthenticationIsUpdating: SettingsStore.isTwoFactorAuthenticationUpdating(),
        twoFactorAuthenticationSuccess: SettingsStore.isTwoFactorAuthenticationSuccess(),
        isAccountDeleted: SettingsStore.isAccountDeleted(),
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      throw e;
    }
  }, []);

  useEffect(() => {
    SettingsStore.addListener('change', onChange);
    setUserData(userData);
    getUserSettings(userData.id, client);
    if (router?.location?.hash) {
      const rawHash = router.location.hash.substr(1);
      const sectionName = Object.values(TopSections).find((v) => v.toLowerCase() === rawHash.toLowerCase());
      if (sectionName) {
        setTopSection(sectionName);
      } else {
        setTopSection(TopSections.ACCOUNT);
      }
    } else {
      setTopSection(TopSections.ACCOUNT);
    }

    onChange();

    document.querySelector('[data-js=origami]')?.remove();

    return () => {
      SettingsStore.removeListener('change', onChange);
    };
  }, [userData, router, client, onChange]);

  useEffect(() => {
    if (storeState.hasPendingChanges) {
      window.onbeforeunload = () => _t('You will lose your unsaved changes.');
    } else {
      window.onbeforeunload = null;
    }
  }, [storeState.hasPendingChanges]);

  const track = useTracker();

  const handleTabChange = useCallback<(value: string) => void>(
    (value: string) => {
      if (!isValidTopSection(value)) return;
      resetAllSections();
      setTopSection(value);

      track('click_tab', {
        tab: { name: ACCOUNT_SETTINGS_TABS_NAME, selectedTab: value.toLowerCase() },
      });

      if (value === TopSections.EMAIL) {
        track('click_button', {
          button: { name: 'communication_preferences' },
        });
      }
    },
    [track]
  );

  const scrollToRecoveryEmail = useCallback<() => void>(() => {
    setTopSection(TopSections.ACCOUNT);
    setTimeout(() => {
      document.getElementById('recovery-email-section')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }, []);

  const { tabsWrapperRef, canScrollBackward, canScrollForward, scrollBackward, scrollForward } =
    useTabScrollAffordance();

  const {
    emailAddress,
    fullName,
    invalidFields,
    updatedEmailAddress,
    userId,
    topSection,
    wallets,
    creditCardPreferences,
    showDeleteCreditCardModal,
    activeWalletId,
    notificationPreferences,
    emailPreferences,
    twoFactorAuthenticationStatus,
    twoFactorAuthenticationQRCode,
    twoFactorAuthenticationInvalidFields,
    twoFactorAuthenticationIsUpdating,
    twoFactorAuthenticationSuccess,
    isAccountDeleted,
    updateStatus,
    timezone,
    locale,
  } = storeState;

  const { isDailyGoalsFeatureEnabled: shouldShowSoundEffects } = useIsDailyGoalsFeatureEnabled();

  if (isAccountDeleted) {
    return <DeletionConfirmation />;
  }

  const ssoOrgName = ssoOrg?.name;

  const isSecondaryEmailPresent = recoveryEmailData && Object.keys(recoveryEmailData).length > 0;

  const showRecoveryEmailSection = isUserSsoLoginForced || isUnifiedSso;
  const showAddRecoveryEmailWarning =
    showRecoveryEmailSection &&
    !isSecondaryEmailPresent &&
    (isUnifiedSso ? gdprSettings && gdprSettings?.gdprMode !== 'GDPR_MODE_RESTRICTIVE' : true);
  const showPersonalAccountAndPasswordSection = showPersonalAccountSection && !showRecoveryEmailSection;
  const showStandAlonePasswordSection =
    showPasswordSection && !showPersonalAccountAndPasswordSection && !isUserSsoLoginForced;

  const renderAccountSection = () => (
    <div css={styles.sections}>
      <SectionCard>
        <BasicInformation
          fullName={fullName}
          emailAddress={emailAddress}
          timezone={timezone ?? ''}
          locale={locale ?? ''}
          updateStatus={getUpdateStatus(updateStatus, Sections.BASIC_INFORMATION)}
          invalidFields={invalidFields ?? []}
          updatedEmailAddress={updatedEmailAddress ?? ''}
          ssoOrgName={ssoOrgName}
        />
      </SectionCard>

      {isMyLearningRedesignEnabled() && (
        <SectionCard>
          <ProfilePhoto />
        </SectionCard>
      )}

      {enableAppearancePreferences() && (
        <SectionCard>
          <AppearancePreferences />
        </SectionCard>
      )}

      {shouldShowSoundEffects && (
        <SectionCard highlightOnHash={SOUND_EFFECTS_HASH}>
          <SoundEffectsField />
        </SectionCard>
      )}

      {showRecoveryEmailSection && (
        <SectionCard>
          <RecoveryEmail
            ssoOrgSlug={ssoOrg?.slug}
            ssoOrgName={ssoOrgName}
            recoveryEmailData={recoveryEmailData}
            onInsertSuccess={setRecoveryEmailData}
          />
        </SectionCard>
      )}
      {showPersonalAccountAndPasswordSection && (
        <>
          <SectionCard>
            <AlternativeEmail
              updateStatus={getUpdateStatus(updateStatus, Sections.PERSONAL_ACCOUNT)}
              secondaryEmail={secondaryEmail}
              hasGeneratedPassword={hasGeneratedPassword}
              ssoOrgSlug={ssoOrg?.slug}
            />
          </SectionCard>
          {showPasswordSection && (
            <SectionCard>
              <Password
                updateStatus={getUpdateStatus(updateStatus, Sections.PASSWORD)}
                invalidFields={invalidFields ?? []}
              />
            </SectionCard>
          )}
        </>
      )}
      <SectionCard>
        <ErrorBoundaryWithTagging componentName="JumioVerificationSettings">
          <JumioVerificationSettings data-test="cds-idv-view" />
        </ErrorBoundaryWithTagging>
      </SectionCard>
      {showStandAlonePasswordSection && (
        <SectionCard>
          <Password
            updateStatus={getUpdateStatus(updateStatus, Sections.PASSWORD)}
            invalidFields={invalidFields ?? []}
          />
        </SectionCard>
      )}
      {!isUserSsoLoginForced && (
        <>
          <SectionCard>
            <TwoFactorAuthentication
              state={twoFactorAuthenticationStatus}
              qrCode={twoFactorAuthenticationQRCode}
              invalidFields={twoFactorAuthenticationInvalidFields}
              updating={twoFactorAuthenticationIsUpdating}
              success={twoFactorAuthenticationSuccess}
              available
            />
          </SectionCard>
          <UserContextSwitcherSection />
          <SectionCard>
            <SessionInvalidation />
          </SectionCard>
          <AccountLinkingPreferences />
        </>
      )}

      <SectionCard>
        <DataReport />
      </SectionCard>
      {showAccountDeletionSection && (
        <SectionCard>
          <AccountDeletion userId={userId} />
        </SectionCard>
      )}
    </div>
  );

  const renderPaymentSection = () => (
    <CreditCardSettings
      creditCardPreferences={creditCardPreferences}
      showDeleteCreditCardModal={showDeleteCreditCardModal}
      activeWalletId={activeWalletId}
      wallets={wallets}
      updateStatusDelete={getUpdateStatus(updateStatus, Sections.CREDIT_CARD_DELETE)}
      userId={userId}
    />
  );

  const renderEmailSection = () =>
    emailPreferences && (
      <EmailPreferences
        globalNotificationsEnabled={notificationPreferences?.global === 'ENABLED'}
        coursePreferences={emailPreferences.courses}
        partnerPreferences={emailPreferences.partners}
        courseraPreferences={emailPreferences.coursera}
        courseUpdateStatus={getUpdateStatus(updateStatus, Sections.COURSE_EMAIL_PREFERENCES)}
        partnerUpdateStatus={getUpdateStatus(updateStatus, Sections.PARTNER_EMAIL_PREFERENCES)}
        courseraUpdateStatus={getUpdateStatus(updateStatus, Sections.COURSERA_EMAIL_PREFERENCES)}
      />
    );

  const renderHighlightsSection = () => <NotesAndHighlightingSettings userId={userId as number} />;

  const renderCalendarSection = () => <CalendarSettings />;

  return (
    <div css={styles.root}>
      <CourseraMetatags metaNameAndProperties={{ title: 'My Account Settings', disableCrawlerIndexing: true }} />
      <UniversalPageFeatures />
      <AccountPageHeader hideMetaNav />
      <AccountSettingsPageProviders>
        <div css={styles.container} data-testid="account-settings-content">
          {showAddRecoveryEmailWarning && (
            <PageNotification
              css={styles.recoveryEmailWarning}
              severity="warning"
              inlineAction={
                <Button size="small" variant="ghost" onClick={scrollToRecoveryEmail}>
                  {_t('Set recovery email')}
                </Button>
              }
            >
              {_t('Add a recovery email to secure your account')}
            </PageNotification>
          )}
          <Typography2
            variant="titleLargeCompact"
            component="h1"
            css={styles.heading}
            data-testid="account-settings-heading"
          >
            {_t('Account settings')}
          </Typography2>
          <TabContext
            value={
              (topSection === TopSections.PAYMENT && (wallets?.length ?? 0) === 0) ||
              (topSection === TopSections.PERSONALIZATION && !isPersonalizationEnabled)
                ? TopSections.ACCOUNT
                : topSection || TopSections.ACCOUNT
            }
          >
            <div ref={tabsWrapperRef} css={styles.tabsWrapper}>
              <Tabs
                variant="primary"
                aria-label={_t('Account settings navigation')}
                onChange={handleTabChange}
                css={styles.tabs}
              >
                {[
                  <Tab key={TopSections.ACCOUNT} label={_t('Account')} value={TopSections.ACCOUNT} />,
                  (wallets?.length || 0) > 0 ? (
                    <Tab key={TopSections.PAYMENT} label={_t('Payment')} value={TopSections.PAYMENT} />
                  ) : null,
                  <Tab key={TopSections.EMAIL} label={_t('Communication Preferences')} value={TopSections.EMAIL} />,
                  isPersonalizationEnabled ? (
                    <Tab
                      key={TopSections.PERSONALIZATION}
                      label={_t('Personalization')}
                      value={TopSections.PERSONALIZATION}
                    />
                  ) : null,
                  <Tab key={TopSections.CALENDAR} label={_t('Calendar Sync')} value={TopSections.CALENDAR} />,
                  <Tab key={TopSections.HIGHLIGHTS} label={_t('Notes & Highlights')} value={TopSections.HIGHLIGHTS} />,
                ].filter(Boolean)}
              </Tabs>
              <TabScrollAffordance
                canScrollBackward={canScrollBackward}
                canScrollForward={canScrollForward}
                onScrollBackward={scrollBackward}
                onScrollForward={scrollForward}
              />
            </div>
            <TabPanel value={TopSections.ACCOUNT}>{renderAccountSection()}</TabPanel>
            {(wallets?.length || 0) > 0 && (
              <TabPanel value={TopSections.PAYMENT}>
                <div css={styles.sections}>
                  <SectionCard>{renderPaymentSection()}</SectionCard>
                </div>
              </TabPanel>
            )}
            <TabPanel value={TopSections.EMAIL}>
              <div css={styles.sections}>
                <SectionCard>{renderEmailSection()}</SectionCard>
              </div>
            </TabPanel>
            {isPersonalizationEnabled && (
              <TabPanel value={TopSections.PERSONALIZATION}>
                <div css={styles.sections}>
                  <PersonalizationTab />
                </div>
              </TabPanel>
            )}
            <TabPanel value={TopSections.CALENDAR}>
              <div css={styles.sections}>
                <SectionCard>{renderCalendarSection()}</SectionCard>
              </div>
            </TabPanel>
            <TabPanel value={TopSections.HIGHLIGHTS}>
              <div css={styles.sections}>
                <SectionCard>{renderHighlightsSection()}</SectionCard>
              </div>
            </TabPanel>
          </TabContext>
        </div>
      </AccountSettingsPageProviders>
      <PageFooter />
    </div>
  );
};

const SettingsAppWrapper = (props: Props) => {
  return (
    <EnterpriseUserFeaturesProvider thirdPartyOrganizationIds={props.thirdPartyOrganizationIds}>
      <EnterpriseUserFeaturesConsumer>
        {({ isUserSsoLoginForced, isUnifiedSso, gdprSettings }) => (
          <SettingsApp
            {...props}
            isUserSsoLoginForced={isUserSsoLoginForced}
            isUnifiedSso={isUnifiedSso}
            gdprSettings={gdprSettings}
          />
        )}
      </EnterpriseUserFeaturesConsumer>
    </EnterpriseUserFeaturesProvider>
  );
};

type PropsFromFindUserEmailsByUserIdQuery = {
  userEmails?: UserEmail[];
};

export default compose<Props, {}>(
  setDisplayName('AccountSettingsHOC'),
  connectToRouter((router) => ({ router })),
  Retracked.createTrackedContainer(() => ({
    namespace: {
      app: 'account_settings',
      page: 'page',
    },
  })),
  connectToStores(['ApplicationStore'], ({ ApplicationStore }, props) => ({
    ...props,
    userData: ApplicationStore.getUserData(),
  })),
  waitForGraphQL<
    {},
    ProgramMembershipsAndThirdPartyAccountsQueryQuery,
    ProgramMembershipsAndThirdPartyAccountsQueryQueryVariables,
    { thirdPartyOrganizationIds: string[]; thirdPartyAccounts: any[] }
  >(ProgramMembershipsAndThirdPartyAccountsQuery, {
    props: ({ data }) => {
      const programMemberships =
        data?.ProgramMemberships?.queryProgramMembershipsByUserIdAndIncludeInvitations?.edges || [];
      const thirdPartyAccounts = data?.NaptimeQueries?.ThirdPartyAccountsV1Resource?.byUser?.elements || [];
      const thirdPartyOrganizationIds =
        programMemberships.map((edge) => edge?.node?.program?.naptimeThirdPartyOrganization?.id).filter(Boolean) || [];
      return { thirdPartyOrganizationIds, thirdPartyAccounts };
    },
    options: () => ({
      variables: {
        userId: user.get().id,
        includeInvitations: true,
      },
      errorPolicy: 'all',
    }),
  }),
  waitForGraphQL<
    {},
    FindUserEmailsByUserIdQueryResult,
    FindUserEmailsByUserIdQueryVariables,
    PropsFromFindUserEmailsByUserIdQuery
  >(FindUserEmailsByUserIdQuery, {
    props: ({ data }) => {
      const userEmails = data?.FindUserEmailsByUserIdResponse?.userEmails || [];
      return { userEmails, refetchUserEmails: data?.refetch };
    },
    options: () => ({
      variables: {
        userId: `${user.get().id}`,
      },
      errorPolicy: 'all',
    }),
  }),
  // @ts-expect-error ts-migrate(2339) FIXME: Property 'thirdPartyAccounts' does not exist on ty... Remove this comment to see the full error message
  Naptime.createContainer(({ thirdPartyAccounts }) => {
    const ssoOrgId = thirdPartyAccounts
      ?.filter((_: $TSFixMe) => _.id === 'saml' || _.id === 'jwtInvite')
      ?.map((_: $TSFixMe) => _.identifier.split('|')[1])[0];

    return {
      gdprSettings:
        ssoOrgId &&
        GdprSettings.get(ssoOrgId, {
          fields: ['recoveryAccountInSettings'],
        }),
      ssoOrg: ssoOrgId
        ? ThirdPartyOrganizationsV1Resource.get(ssoOrgId, {
            fields: ['name', 'slug'],
          })
        : undefined,
    };
  }),
  // @ts-expect-error ts-migrate(2345) FIXME: Argument of type '({ userEmails, thirdPartyOrganiz... Remove this comment to see the full error message
  withProps(({ userEmails, thirdPartyOrganizationIds, gdprSettings }) => {
    const showPersonalAccountSection =
      gdprSettings?.recoveryAccountInSettings !== false && thirdPartyOrganizationIds.length > 0;
    const showAccountDeletionSection = !thirdPartyOrganizationIds.includes('_khIP87SRomhUydEliGbnw');
    const hasGeneratedPassword = user.get().generated_password;
    const secondaryEmail = getSecondaryEmail(userEmails);
    return {
      showPersonalAccountSection,
      showPasswordSection: !hasGeneratedPassword,
      showAccountDeletionSection,
      secondaryEmail,
      hasGeneratedPassword,
      userEmails,
    };
  }),
  withRecoveryEmail,
  withApollo
)(SettingsAppWrapper);
